import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import styles from "./ExchangeOffersPanel.module.scss";

type QuoteItem = {
  quote_id: string;
  provider: string;
  label: string;
  badge?: string | null;
  receive_amount: string;
  rate?: string | null;
  rate_mode: string;
  eta?: string | null;
  fee_included: boolean;
  provider_fee?: string | null;
  network_fee?: string | null;
  alfa_fee: string;
  send_value_usd?: string | null;
  receive_value_usd?: string | null;
  value_loss_usd?: string | null;
  value_loss_percent?: string | null;
  quote_warning?: string | null;
  kyc_risk?: string | null;
  expires_at?: string | null;
  raw?: Record<string, unknown>;
};

type ExchangeOffersPanelProps = {
  activeComponent: string;
  selectedPay: string;
  selectedReceive: string;
  payAmount: string;
  fromCode?: string;
  toCode?: string;
  selectedQuoteId?: string;
  onQuoteSelect?: (quote: QuoteItem) => void;
  onLoadingChange?: (isLoading: boolean) => void;
};

type QuoteCostRequest = {
  amount: string;
  from_asset: string;
  to_asset: string;
};

type DetailRow = {
  label: string;
  value: string;
};

const NETWORK_ALIASES: Record<string, string> = {
  TRC20: "TRC20",
  ERC20: "ERC20",
  BEP20: "BEP20",
};

const ERC20_SYMBOLS = new Set([
  "ETH",
  "USDT",
  "USDC",
  "TUSD",
  "WBTC",
  "DAI",
  "LINK",
  "YFI",
  "BAT",
  "UNI",
  "USDP",
  "ZRX",
  "MKR",
  "OMG",
]);

const NATIVE_NETWORK_SYMBOLS = new Set([
  "BTC",
  "SOL",
  "XRP",
  "ADA",
  "DOT",
  "LTC",
  "XMR",
  "DOGE",
  "TRX",
  "BCH",
  "XLM",
  "ATOM",
  "DASH",
  "ZEC",
  "EOS",
  "QTUM",
  "XEM",
]);

const extractSymbol = (label: string, code?: string) => {
  const match = label.match(/\(([A-Z0-9]+)\)/);
  if (match?.[1]) return match[1];
  return (code || label).split(" ")[0].replace(/[^A-Z0-9]/g, "");
};

const extractNetwork = (code?: string) => {
  if (!code) return "MAINNET";
  const normalized = code.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (normalized.includes("TRC20")) return "TRC20";
  if (normalized.includes("ERC20")) return "ERC20";
  if (normalized.includes("BEP20")) return "BEP20";
  if (ERC20_SYMBOLS.has(normalized)) return "ERC20";
  if (NATIVE_NETWORK_SYMBOLS.has(normalized)) return normalized;
  const parts = code.toUpperCase().split(/\s+/);
  const network = parts.find((part) => NETWORK_ALIASES[part]);
  return network || "MAINNET";
};

const formatAmount = (value: string) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  if (numeric >= 100) return numeric.toFixed(2);
  if (numeric >= 1) return numeric.toFixed(6).replace(/\.?0+$/, "");
  return numeric.toFixed(8).replace(/\.?0+$/, "");
};

const subtractDecimalAmounts = (value: string, deduction?: string | null) => {
  const normalize = (input: string) => {
    const [whole = "0", fraction = ""] = input.trim().split(".");
    return { whole: whole || "0", fraction };
  };

  try {
    const left = normalize(value);
    const right = normalize(deduction || "0");
    const scale = Math.max(left.fraction.length, right.fraction.length);
    const toMinor = ({ whole, fraction }: ReturnType<typeof normalize>) =>
      BigInt(`${whole}${fraction.padEnd(scale, "0")}`);
    const result = toMinor(left) - toMinor(right);

    if (result <= BigInt(0)) return "0";
    if (scale === 0) return result.toString();

    const padded = result.toString().padStart(scale + 1, "0");
    const whole = padded.slice(0, -scale);
    const fraction = padded.slice(-scale).replace(/0+$/, "");
    return fraction ? `${whole}.${fraction}` : whole;
  } catch {
    return value;
  }
};

const getNetReceiveAmount = (quote: QuoteItem) =>
  subtractDecimalAmounts(quote.receive_amount, quote.alfa_fee);

const formatUsd = (value?: string | null) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `$${numeric.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatPercent = (value?: string | null) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${numeric.toFixed(2).replace(/\.?0+$/, "")}%`;
};

const getQuoteCostRows = (quote: QuoteItem, request: QuoteCostRequest) => {
  return [
    {
      label: "You send",
      value: `${formatAmount(request.amount)} ${request.from_asset}`,
    },
    {
      label: "You receive",
      value: `${formatAmount(getNetReceiveAmount(quote))} ${request.to_asset}`,
    },
  ];
};

const getQuoteDetailRows = (
  quote: QuoteItem,
  request: QuoteCostRequest
): DetailRow[] => {
  const rows: DetailRow[] = [];
  const rate = Number(getNetReceiveAmount(quote)) / Number(request.amount);
  const sendValue = formatUsd(quote.send_value_usd);
  const receiveValue = formatUsd(quote.receive_value_usd);
  const valueDifference = formatPercent(quote.value_loss_percent);

  if (Number.isFinite(rate) && rate > 0) {
    rows.push({
      label: "Rate",
      value: `1 ${request.from_asset} = ${formatAmount(String(rate))} ${request.to_asset}`,
    });
  }

  if (sendValue && receiveValue) {
    rows.push({
      label: "Estimated value",
      value: `${sendValue} -> ${receiveValue}`,
    });
  }

  if (valueDifference) {
    rows.push({
      label: "Value difference",
      value: valueDifference,
    });
  }

  rows.push({
    label: "Network fee",
    value: "Paid by your wallet",
  });

  return rows;
};

const REFRESH_INTERVAL_SECONDS = 30;

const getVerificationLabel = (quote: QuoteItem) => {
  const kycRisk = (quote.kyc_risk || "").toLowerCase();
  if (kycRisk === "possible" || kycRisk === "high") {
    return "May be required";
  }
  return "Not expected";
};

const ExchangeOffersPanel: FC<ExchangeOffersPanelProps> = ({
  activeComponent,
  selectedPay,
  selectedReceive,
  payAmount,
  fromCode,
  toCode,
  selectedQuoteId,
  onQuoteSelect,
  onLoadingChange,
}) => {
  const [quotes, setQuotes] = useState<QuoteItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [secondsToRefresh, setSecondsToRefresh] = useState(30);
  const [expandedQuoteId, setExpandedQuoteId] = useState<string | null>(null);
  const onQuoteSelectRef = useRef(onQuoteSelect);
  const onLoadingChangeRef = useRef(onLoadingChange);
  const selectedQuoteIdRef = useRef(selectedQuoteId);
  const selectedProviderRef = useRef<string | null>(null);
  const quotesRef = useRef<QuoteItem[]>([]);

  useEffect(() => {
    onQuoteSelectRef.current = onQuoteSelect;
    onLoadingChangeRef.current = onLoadingChange;
    selectedQuoteIdRef.current = selectedQuoteId;
  }, [onQuoteSelect, onLoadingChange, selectedQuoteId]);

  const normalizedRequest = useMemo(() => {
    const amount = Number(payAmount);
    if (
      activeComponent !== "crypto-crypto" ||
      !selectedPay ||
      !selectedReceive ||
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      return null;
    }

    return {
      from_asset: extractSymbol(selectedPay, fromCode),
      from_network: extractNetwork(fromCode),
      to_asset: extractSymbol(selectedReceive, toCode),
      to_network: extractNetwork(toCode),
      amount: String(amount),
      rate_mode: "floating",
    };
  }, [activeComponent, selectedPay, selectedReceive, payAmount, fromCode, toCode]);

  useEffect(() => {
    if (!normalizedRequest) {
      quotesRef.current = [];
      setQuotes([]);
      setError("");
      setSecondsToRefresh(REFRESH_INTERVAL_SECONDS);
      onLoadingChangeRef.current?.(false);
      return;
    }

    quotesRef.current = [];
    selectedProviderRef.current = null;
    setQuotes([]);
    setSecondsToRefresh(REFRESH_INTERVAL_SECONDS);
    const countdown = window.setInterval(() => {
      setSecondsToRefresh((current) =>
        current <= 1 ? REFRESH_INTERVAL_SECONDS : current - 1
      );
    }, 1000);
    const refresh = window.setInterval(() => {
      setRefreshNonce((current) => current + 1);
    }, REFRESH_INTERVAL_SECONDS * 1000);

    return () => {
      window.clearInterval(countdown);
      window.clearInterval(refresh);
    };
  }, [normalizedRequest]);

  useEffect(() => {
    if (!normalizedRequest) {
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      const isInitialLoad = quotesRef.current.length === 0;
      setIsLoading(true);
      onLoadingChangeRef.current?.(isInitialLoad);
      setError("");
      try {
        const response = await fetch("/api/v1/exchange/quotes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(normalizedRequest),
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Quote request failed: ${response.status}`);
        }
        const data = await response.json();
        const nextQuotes = Array.isArray(data.quotes)
          ? data.quotes.filter((quote: QuoteItem) =>
              ["simpleswap", "changenow", "exolix"].includes(
                String(quote.provider || "").toLowerCase()
              )
            )
          : [];
        quotesRef.current = nextQuotes;
        setQuotes(nextQuotes);
        const selectedQuote = nextQuotes.find(
          (quote: QuoteItem) =>
            quote.quote_id === selectedQuoteIdRef.current ||
            quote.provider === selectedProviderRef.current
        );
        if (selectedQuote) {
          selectedProviderRef.current = selectedQuote.provider;
          onQuoteSelectRef.current?.(selectedQuote);
        } else if (!selectedQuoteIdRef.current) {
          const firstExecutableQuote = nextQuotes.find(
            (quote: QuoteItem) => quote.provider !== "exolix"
          );
          if (firstExecutableQuote) {
            selectedProviderRef.current = firstExecutableQuote.provider;
            onQuoteSelectRef.current?.(firstExecutableQuote);
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          if (isInitialLoad) {
            quotesRef.current = [];
            setQuotes([]);
            setError("Offers are temporarily unavailable");
          }
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          onLoadingChangeRef.current?.(false);
        }
      }
    }, 350);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [normalizedRequest, refreshNonce]);

  const handleQuoteSelect = (quote: QuoteItem) => {
    if (quote.provider === "exolix") return;
    selectedProviderRef.current = quote.provider;
    onQuoteSelect?.(quote);
    window.setTimeout(() => {
      const button = document.querySelector("[data-exchange-submit]");
      button?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  if (!normalizedRequest) return null;

  return (
    <div className={styles.offers}>
      <div className={styles.offers__header}>
        <div>
          <div className={styles.offers__title}>Best offers</div>
          <div className={styles.offers__subtitle}>
            Live quote · updates in {secondsToRefresh}s
          </div>
        </div>
        <div className={styles.offers__mode}>Floating</div>
      </div>

      {isLoading && quotes.length === 0 && (
        <div className={styles.offers__state}>Loading offers...</div>
      )}
      {!isLoading && error && quotes.length === 0 && (
        <div className={styles.offers__state}>{error}</div>
      )}
      {!isLoading && !error && quotes.length === 0 && (
        <div className={styles.offers__state}>No offers for this pair yet</div>
      )}

      {quotes.length > 0 &&
        quotes.map((quote, index) => {
          const detailRows = getQuoteDetailRows(quote, normalizedRequest);
          const isExpanded = expandedQuoteId === quote.quote_id;
          const isQuoteOnly = quote.provider === "exolix";

          return (
            <div
              className={`${styles.offer} ${isQuoteOnly ? styles.offer__quoteOnly : ""} ${
                selectedQuoteId === quote.quote_id ? styles.offer__selected : ""
              }`}
              key={quote.quote_id}
              role="button"
              tabIndex={0}
              aria-disabled={isQuoteOnly}
              onClick={() => handleQuoteSelect(quote)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  handleQuoteSelect(quote);
                }
              }}
            >
              <div className={styles.offer__rank}>{index + 1}</div>
              <div className={styles.offer__main}>
                <div className={styles.offer__provider}>
                  {quote.label}
                  {index === 0 && (
                    <span className={styles.offer__bestRateBadge}>Best rate</span>
                  )}
                  {isQuoteOnly && (
                    <span className={styles.offer__quoteOnlyBadge}>Quote only</span>
                  )}
                </div>
                <div className={styles.offer__costs}>
                  {getQuoteCostRows(quote, normalizedRequest).map(
                    (row) => (
                      <div className={styles.offer__costItem} key={row.label}>
                        <span>{row.label}</span>
                        <strong>{row.value}</strong>
                      </div>
                    )
                  )}
                </div>
              </div>
              <div className={styles.offer__side}>
                <div className={styles.offer__info}>
                  <div className={styles.offer__fact}>
                    <span>Estimated time</span>
                    <strong>{quote.eta || "Not specified"}</strong>
                  </div>
                  <div className={styles.offer__fact}>
                    <span>Verification</span>
                    <strong>{getVerificationLabel(quote)}</strong>
                  </div>
                  {detailRows.length > 0 && (
                    <button
                      className={styles.offer__detailsToggle}
                      type="button"
                      aria-expanded={isExpanded}
                      onClick={(event) => {
                        event.stopPropagation();
                        setExpandedQuoteId(isExpanded ? null : quote.quote_id);
                      }}
                    >
                      Quote details
                      <span>{isExpanded ? "↑" : "›"}</span>
                    </button>
                  )}
                </div>
                {isExpanded && detailRows.length > 0 && (
                  <div
                    className={styles.offer__detailsPanel}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {detailRows.map((row) => (
                      <div className={styles.offer__detailsRow} key={row.label}>
                        <span>{row.label}</span>
                        <strong>{row.value}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default ExchangeOffersPanel;
